import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Flex,
  Box,
  Heading,
  Text,
  Button,
  TextField,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  DataTable,
  LoadingOrbit,
  User as UserIcon,
  Card,
  CardHeader,
  CardContent,
  CheckCircle,
  Warning,
  Page,
  Banner,
  Select,
  Switch,
  useToast,
  DangerSection,
  DangerAction,
  DocsAnnotation,
  EditableBadge,
  TagBadge,
  MetadataItem,
  type EditableBadgeOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Tooltip, TooltipTrigger } from 'react-aria-components';
import { SlackLogo, GithubLogo, Trash, ArrowSquareOut, ShieldSlash } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { API_URL } from '../../config.js';
import { parseApiJson } from '../../lib/api-error';
import { deepLinkTargetsActiveOrg, resolveOrgDeepLink } from '../../lib/organization-context';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../brand';
import { DomainAllowlistManager } from '../components/inbox/DomainAllowlistManager';
import { IssueCategoriesSection } from '../components/settings/IssueCategoriesSection';
import { TagsSection } from '../components/settings/TagsSection';
import { NotificationsSection } from '../components/settings/NotificationsSection';
import { DigestsSection } from '../components/settings/DigestsSection';
import { ApiKeysSection } from '../components/settings/ApiKeysSection';
import { SecuritySection } from '../components/settings/SecuritySection';
import { OrgMfaRequirementToggle } from '../components/settings/OrgMfaRequirementToggle';
import { PlanFeaturesCard } from '../components/settings/PlanFeaturesCard';
import { CloudflareAccessSection } from '../components/settings/CloudflareAccessSection';
import { AwsConnectionSection } from '../components/settings/AwsConnectionSection';
import { WorkflowsSection } from '../components/settings/WorkflowsSection';
import { ObservabilitySection } from '../components/settings/ObservabilitySection';
import { AgentBrowserSection } from '../components/settings/AgentBrowserSection';
import { DocumentationSection } from '../components/settings/DocumentationSection';
import { UsageSection } from '../components/settings/UsageSection';
import { BillingSection } from '../components/settings/BillingSection';
import { LinearSettingsSection } from '../components/settings/LinearSettingsSection';
import { ClaudeSettingsSection } from '../components/settings/ClaudeSettingsSection';
import { TeamsSettingsSection, fetchTeamsConnection, startTeamsInstall } from '../components/settings/TeamsSettingsSection';
import { JiraSettingsSection } from '../components/settings/JiraSettingsSection';
import { AzureDevopsSettingsSection } from '../components/settings/AzureDevopsSettingsSection';
import { CustomDomainsSection } from '../components/settings/CustomDomainsSection';
import { WorkspaceAddressRow } from '../components/settings/WorkspaceAddressRow';
import { CopyValueButton } from '../components/settings/CopyValueButton';
import { FeatureFlagsSection } from '../components/settings/FeatureFlagsSection';
import { TestSignInSection } from '../components/settings/test-sign-in/TestSignInSection';
import {
  fetchOrganization,
  fetchOrganizationMembers,
  fetchOrganizationStats,
  fetchPendingInvites,
  type Member,
  type PendingInvite,
} from './settingsApi';
import { sendSlackTestMessage } from './settingsSlackApi';
import octocatImage from '../../assets/octocat.png';

type JiraConnection = {
  ok: boolean;
  details?: any;
};

type SlackConnection = {
  ok: boolean;
  teamName?: string;
  channelName?: string;
  error?: string;
};

type SlackChannel = {
  id: string;
  name: string;
  is_private?: boolean;
  is_member?: boolean;
};

type SlackIntegrationSettings = {
  requireUserLinking?: boolean;
  codexChannelId?: string;
  codexOwnerSlackUserId?: string;
};

type GitHubConnection = {
  ok: boolean;
  accountLogin?: string;
  accountType?: string;
  installationId?: number;
  error?: string;
};

type GitHubRepository = {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  htmlUrl: string;
};

// Helper component to wrap buttons that require admin access
function AdminButton({
  isAdmin,
  tooltipText,
  children,
}: {
  isAdmin: boolean;
  tooltipText: string;
  children: React.ReactElement<{ isDisabled?: boolean }>;
}) {
  if (isAdmin) {
    return children;
  }

  return (
    <TooltipTrigger delay={0}>
      {React.cloneElement(children, { isDisabled: true })}
      <Tooltip
        offset={8}
        placement="bottom"
        className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
      >
        {tooltipText}
      </Tooltip>
    </TooltipTrigger>
  );
}

type SettingsSection =
  | 'organization'
  | 'jira'
  | 'security'
  | 'linear'
  | 'azure-devops'
  | 'claude'
  | 'slack'
  | 'teams'
  | 'github'
  | 'team'
  | 'issue-categories'
  | 'tags'
  | 'workflows'
  | 'notifications'
  | 'digests'
  | 'api-keys'
  | 'test-sign-in'
  | 'cloudflare-access'
  | 'custom-domains'
  | 'aws'
  | 'features'
  | 'inbox'
  | 'documentation'
  | 'observability'
  | 'agent-browser'
  | 'usage'
  | 'billing';

const GITHUB_INSTALL_FALLBACK_ERROR =
  "GitHub couldn't be connected. Try again, and contact support if it keeps happening.";

const GITHUB_ONE_ORG_EXPLAINER =
  'Each GitHub account can connect to one Cofactor organization. If the account you pick already uses Cofactor in another organization, GitHub opens its settings instead of installing again.';

const GITHUB_SELECTION_SAVED_NOTE =
  "Any repository selection changes you made on GitHub are saved on GitHub's side.";

const GITHUB_INSTALL_ERRORS: Record<string, (githubOrg: string | null) => string> = {
  installation_bound_elsewhere: (githubOrg) =>
    `This GitHub account's Cofactor App is ${
      githubOrg
        ? `connected to another Cofactor organization: ${githubOrg}.`
        : 'already connected to another Cofactor organization.'
    } An installation can belong to one organization. ${GITHUB_SELECTION_SAVED_NOTE} ${GITHUB_ONE_ORG_EXPLAINER}`,
  installation_not_connected: () =>
    `This GitHub account already has the Cofactor App installed, but it isn't connected to this organization. It may belong to another Cofactor organization. ${GITHUB_SELECTION_SAVED_NOTE} ${GITHUB_ONE_ORG_EXPLAINER}`,
  missing_authorization: () =>
    "GitHub didn't send an authorization. Reinstall the app from GitHub and approve the authorization step.",
  invalid_state: () => 'This link expired. Start Connect GitHub again.',
  not_installation_admin: () =>
    "Your GitHub account can't access that installation. Install the app on an account or organization you can access.",
  github_verification_failed: () =>
    "GitHub couldn't verify your access to that installation. Select Connect GitHub again, and contact support if it keeps happening.",
};

/** The Slack OAuth start (Settings → Add to Slack, and `connect=slack` from `cofactor slack connect`). */
function startSlackOAuth(): void {
  window.location.href = `${API_URL}/integrations/slack/oauth/start`;
}

/** The GitHub App install start (Settings → Install/Manage, and `connect=github` from `cofactor github connect`). */
function startGithubInstall(): void {
  window.location.href = `${API_URL}/integrations/github/install/start`;
}

export function Settings() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { section?: string };
  const { user } = useAuth();
  const name = BRAND.name;
  const canManageMembers = user?.role === 'admin' || user?.isSuperadmin === true;
  const sectionParam = params.section as SettingsSection | undefined;

  // Breadcrumbs
  const availableSections = React.useMemo<SettingsSection[]>(() => {
    const base: SettingsSection[] = [
      'organization',
      'jira',
      'security',
      'slack',
      'teams',
      'github',
      'team',
      'issue-categories',
      'tags',
      'workflows',
      'api-keys',
      'cloudflare-access',
      'documentation',
      'observability',
      'agent-browser',
      'usage',
      'digests',
    ];
    if (user?.features?.linear) {
      base.push('linear');
    }
    if (user?.features?.azureDevops) {
      base.push('azure-devops');
    }
    if (user?.features?.aws) {
      base.push('aws');
    }
    base.push('claude');
    base.push('notifications');
    if (user?.features?.sandboxCustomDomains) {
      base.push('custom-domains');
    }
    if (user?.features?.testSignIn) {
      base.push('test-sign-in');
    }
    if (user?.canViewBilling) {
      base.push('billing');
    }
    if (user?.isSuperadmin) {
      base.push('features', 'inbox');
    }
    return base;
  }, [
    user?.isSuperadmin,
    user?.canViewBilling,
    user?.features?.linear,
    user?.features?.azureDevops,
    user?.features?.aws,
    user?.features?.sandboxCustomDomains,
    user?.features?.testSignIn,
  ]);

  const githubParams = new URLSearchParams(window.location.search);
  const githubErrorCode = githubParams.get('github_error');
  const githubForOrg = githubParams.get('github_for_org');
  // Only show the rejection on the org the install flow started from; while an
  // org switch is pending the effective org differs and nothing is shown.
  const githubErrorForCurrentOrg =
    !!githubForOrg && !!user?.orgId && githubForOrg.toLowerCase() === user.orgId.toLowerCase();
  const githubInstallError = githubErrorCode && githubErrorForCurrentOrg
    ? (GITHUB_INSTALL_ERRORS[githubErrorCode]?.(githubParams.get('github_org')) ??
      GITHUB_INSTALL_FALLBACK_ERROR)
    : null;
  const currentSection =
    sectionParam && availableSections.includes(sectionParam) ? sectionParam : 'organization';

  const breadcrumbs = React.useMemo(() => {
    const base = [
      {
        label: 'Home',
        onPress: () => navigate({ to: '/' }),
      },
      {
        label: 'Settings',
        onPress: () => navigate({ to: '/settings' }),
      },
    ];
    return base;
  }, [currentSection, navigate]);

  useBreadcrumbs(breadcrumbs);

  // Fetch current organization
  const orgQuery = useQuery({
    queryKey: ['org'],
    queryFn: () => fetchOrganization(),
  });

  // Fetch organization stats
  const orgStatsQuery = useQuery({
    queryKey: ['org-stats'],
    queryFn: () => fetchOrganizationStats(),
  });

  // Fetch Jira connection status
  const jiraQuery = useQuery({
    queryKey: ['jira-validate'],
    queryFn: async (): Promise<JiraConnection> => {
      const res = await fetch(`${API_URL}/integrations/jira/validate`, {
        credentials: 'include',
      });
      return parseApiJson<JiraConnection>(res, 'Failed to validate Jira integration');
    },
  });

  // Fetch Linear connection status
  const linearQuery = useQuery({
    queryKey: ['linear-validate'],
    queryFn: async (): Promise<{ ok: boolean; details?: unknown }> => {
      const res = await fetch(`${API_URL}/integrations/linear/validate`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; details?: unknown }>(
        res,
        'Failed to validate Linear integration'
      );
    },
    enabled: !!user?.features?.linear,
  });

  // Fetch Azure DevOps connection status via the generic ticketing status
  // endpoint (no provider-specific /validate route). Normalize to `{ ok }`.
  const azureDevopsQuery = useQuery({
    queryKey: ['azure-devops-status'],
    queryFn: async (): Promise<{ ok: boolean }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/azureDevops/status`, {
        credentials: 'include',
      });
      const json = await parseApiJson<{ ok: boolean; data?: { connected?: boolean } }>(
        res,
        'Failed to validate Azure DevOps integration'
      );
      return { ok: !!json?.data?.connected };
    },
    enabled: !!user?.features?.azureDevops,
  });

  // Fetch Slack connection status
  const slackQuery = useQuery({
    queryKey: ['slack-validate'],
    queryFn: async (): Promise<SlackConnection> => {
      const res = await fetch(`${API_URL}/integrations/slack/validate`, {
        credentials: 'include',
      });
      return parseApiJson<SlackConnection>(res, 'Failed to validate Slack integration');
    },
  });

  // The URL this page was opened with: AuthContext strips `cofactor_org_id` from the live URL
  // once /me loads, and a connect must still know which org the link named.
  const entryHref = React.useRef(window.location.href);

  // `cofactor slack connect` opens `/settings/slack?cofactor_org_id=<org>&connect=slack`.
  // When AuthContext is about to move this browser to another org (a full
  // reload that keeps `connect`), wait for that page. Otherwise the session is
  // already in the right org (or the org id was unusable and AuthContext strips
  // it): drop `connect` and, once Slack reports "not connected", start OAuth. A
  // status check that fails outright is left to the page's own error state.
  // Keyed on `user` and the query, so it re-checks when either settles; once
  // per load.
  const slackConnectHandled = React.useRef(false);
  React.useEffect(() => {
    if (slackConnectHandled.current || !user || slackQuery.isPending) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') !== 'slack') return;
    if (resolveOrgDeepLink(window.location.href, user).action === 'switch') return;
    slackConnectHandled.current = true;
    const startHere = deepLinkTargetsActiveOrg(entryHref.current, user);
    params.delete('connect');
    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
    if (startHere && slackQuery.isSuccess && !slackQuery.data.ok) startSlackOAuth();
  }, [user, slackQuery.isPending, slackQuery.isSuccess, slackQuery.data]);

  // Fetch Slack integration settings (user-linking and Codex routing).
  const slackSettingsQuery = useQuery({
    queryKey: ['slack-settings'],
    queryFn: async (): Promise<{
      ok: boolean;
      codexEligible: boolean;
      settings: SlackIntegrationSettings;
    }> => {
      const res = await fetch(`${API_URL}/integrations/slack/settings`, {
        credentials: 'include',
      });
      return parseApiJson<{
        ok: boolean;
        codexEligible: boolean;
        settings: SlackIntegrationSettings;
      }>(res, 'Failed to fetch Slack settings');
    },
    enabled: slackQuery.data?.ok === true,
  });

  // Fetch Slack channels (only when connected)
  const slackChannelsQuery = useQuery({
    queryKey: ['slack-channels'],
    queryFn: async (): Promise<{ ok: boolean; channels?: SlackChannel[]; error?: string }> => {
      const res = await fetch(`${API_URL}/integrations/slack/channels`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; channels?: SlackChannel[]; error?: string }>(
        res,
        'Failed to fetch Slack channels'
      );
    },
    enabled: slackQuery.data?.ok === true,
  });

  // Fetch GitHub connection status
  const githubQuery = useQuery({
    queryKey: ['github-status'],
    queryFn: async (): Promise<GitHubConnection> => {
      const res = await fetch(`${API_URL}/integrations/github/status`, {
        credentials: 'include',
      });
      return parseApiJson<GitHubConnection>(res, 'Failed to fetch GitHub status');
    },
  });

  // `cofactor github connect` opens `/settings/github?cofactor_org_id=<org>&connect=github`.
  // Same hand-off as `connect=slack` above, with two conditions on starting: the viewer
  // is allowed (`user.canCreateOrganization`: the `plg.self_serve_org_creation` knob, or a
  // superadmin; with it off the page shows normally and the visitor presses Install GitHub
  // App themselves) and the link names this org or none (an org id the viewer can't reach
  // is stripped, not switched to). `connect` is stripped either way. `{ok:false}` alone is
  // "not connected"; with an error the existing connection could not be verified, so
  // nothing starts.
  const githubConnectHandled = React.useRef(false);
  React.useEffect(() => {
    if (githubConnectHandled.current || !user || githubQuery.isPending) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') !== 'github') return;
    if (resolveOrgDeepLink(window.location.href, user).action === 'switch') return;
    githubConnectHandled.current = true;
    const startHere = deepLinkTargetsActiveOrg(entryHref.current, user);
    params.delete('connect');
    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
    const notConnected = githubQuery.isSuccess && !githubQuery.data.ok && !githubQuery.data.error;
    if (startHere && user.canCreateOrganization && canManageMembers && notConnected) startGithubInstall();
  }, [user, canManageMembers, githubQuery.isPending, githubQuery.isSuccess, githubQuery.data]);

  // `cofactor teams install` opens `/settings/teams?cofactor_org_id=<org>&connect=teams`.
  // Same hand-off and conditions as `connect=github` above, with one more: Teams is
  // enabled per org (`isFlagEnabled`), and an org without it just sees the page. The
  // connection is only read for a link that asks for the connect. Already in the tenant
  // catalog means nothing to start; a bound tenant not yet in the catalog still starts
  // (the install publishes the manifest). `connect` is stripped either way.
  const teamsConnectHandled = React.useRef(false);
  const wantsTeamsConnect = React.useRef(new URLSearchParams(window.location.search).get('connect') === 'teams');
  const teamsConnectQuery = useQuery({
    queryKey: ['teams-connection'],
    queryFn: fetchTeamsConnection,
    enabled: wantsTeamsConnect.current && !!user,
  });
  React.useEffect(() => {
    if (teamsConnectHandled.current || !user || teamsConnectQuery.isPending) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') !== 'teams') return;
    if (resolveOrgDeepLink(window.location.href, user).action === 'switch') return;
    teamsConnectHandled.current = true;
    const startHere = deepLinkTargetsActiveOrg(entryHref.current, user);
    params.delete('connect');
    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
    const startable =
      teamsConnectQuery.isSuccess && teamsConnectQuery.data.isFlagEnabled && !teamsConnectQuery.data.isInCatalog;
    if (startHere && user.canCreateOrganization && canManageMembers && startable) startTeamsInstall();
  }, [user, canManageMembers, teamsConnectQuery.isPending, teamsConnectQuery.isSuccess, teamsConnectQuery.data]);

  // Fetch GitHub repositories (only when connected)
  const githubReposQuery = useQuery({
    queryKey: ['github-repositories'],
    queryFn: async (): Promise<{
      ok: boolean;
      repositories?: GitHubRepository[];
      error?: string;
    }> => {
      const res = await fetch(`${API_URL}/integrations/github/repositories`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; repositories?: GitHubRepository[]; error?: string }>(
        res,
        'Failed to fetch GitHub repositories'
      );
    },
    enabled: githubQuery.data?.ok === true,
  });

  // Fetch team members
  const membersQuery = useQuery({
    queryKey: ['org-members'],
    queryFn: () => fetchOrganizationMembers(),
  });

  // Fetch pending (unused, non-expired) invitations
  const pendingInvitesQuery = useQuery({
    queryKey: ['org-invites-pending'],
    queryFn: () => fetchPendingInvites(),
  });

  // Organization update state
  const [isEditOrgOpen, setIsEditOrgOpen] = React.useState(false);
  const [orgName, setOrgName] = React.useState('');

  React.useEffect(() => {
    if (orgQuery.data) {
      setOrgName(orgQuery.data.name);
    }
  }, [orgQuery.data]);

  const updateOrgMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`${API_URL}/org`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      setIsEditOrgOpen(false);
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  // Delete organization state
  const { toast } = useToast();
  const { signOut } = useAuth();
  const [isDeleteOrgOpen, setIsDeleteOrgOpen] = React.useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = React.useState('');

  const deleteOrgMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/org`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete organization');
      }
      return res.json();
    },
    onSuccess: () => {
      setIsDeleteOrgOpen(false);
      toast.success('Organization deleted successfully');
      // Sign out the user since their org no longer exists
      signOut();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Invite state
  const [isInviteDialogOpen, setIsInviteDialogOpen] = React.useState(false);
  const [inviteLink, setInviteLink] = React.useState('');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<'admin' | 'member'>('member');
  const [inviteEmailSent, setInviteEmailSent] = React.useState(false);

  const disconnectJiraMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/jira`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jira-validate'] });
      qc.invalidateQueries({ queryKey: ['jira-validate-shell'] });
    },
  });

  const disconnectLinearMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/linear`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['linear-validate'] });
    },
  });

  const disconnectAzureDevopsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/azure-devops`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['azure-devops-status'] });
      qc.invalidateQueries({ queryKey: ['azure-devops-settings'] });
    },
  });

  const disconnectSlackMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/slack`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-validate'] });
      qc.invalidateQueries({ queryKey: ['slack-channels'] });
    },
  });

  const disconnectGitHubMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/github`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['github-status'] });
      qc.invalidateQueries({ queryKey: ['github-repositories'] });
    },
  });

  const updateSlackChannelMutation = useMutation({
    mutationFn: async ({ channelId, channelName }: { channelId: string; channelName?: string }) => {
      const res = await fetch(`${API_URL}/integrations/slack/channel`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ channelId, channelName }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-validate'] });
    },
  });

  const updateSlackSettingsMutation = useMutation({
    mutationFn: async (settings: { requireUserLinking: boolean }) => {
      const res = await fetch(`${API_URL}/integrations/slack/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-settings'] });
    },
  });

  const updateCodexSlackSettingsMutation = useMutation({
    mutationFn: async (settings: { codexChannelId: string; codexOwnerSlackUserId: string }) => {
      const res = await fetch(`${API_URL}/integrations/slack/codex-settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-settings'] });
    },
  });

  const sendSlackTestMutation = useMutation({
    mutationFn: sendSlackTestMessage,
  });

  const [codexSettingsDraft, setCodexSettingsDraft] = React.useState({
    channelId: '',
    ownerSlackUserId: '',
  });

  React.useEffect(() => {
    const settings = slackSettingsQuery.data?.settings;
    if (settings) {
      setCodexSettingsDraft({
        channelId: settings.codexChannelId ?? '',
        ownerSlackUserId: settings.codexOwnerSlackUserId ?? '',
      });
    }
  }, [slackSettingsQuery.data]);

  const createInviteMutation = useMutation({
    mutationFn: async (params?: { email?: string; role?: 'admin' | 'member' }) => {
      const res = await fetch(`${API_URL}/org/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: params?.email, role: params?.role ?? 'member' }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (data, params) => {
      const link = data.inviteUrl;
      setInviteLink(link);
      if (params?.email && data.emailSent) {
        setInviteEmailSent(true);
      }
      qc.invalidateQueries({ queryKey: ['org-invites-pending'] });
    },
  });

  const roleBadgeOptions = React.useMemo<EditableBadgeOption<Member['role']>[]>(
    () => [
      { id: 'admin', label: 'Admin' },
      { id: 'member', label: 'Member' },
    ],
    []
  );
  const [roleOverrides, setRoleOverrides] = React.useState<Record<string, Member['role']>>({});
  const [updatingMemberId, setUpdatingMemberId] = React.useState<string | null>(null);

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: Member['role'] }) => {
      const res = await fetch(`${API_URL}/org/members/${memberId}/role`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-members'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`${API_URL}/org/members/${memberId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to remove member');
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-members'] });
    },
  });

  const resetMemberMfaMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`${API_URL}/org/members/${memberId}/mfa`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to reset MFA');
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-members'] });
    },
  });

  const handleRoleChange = React.useCallback(
    (member: Member, nextRole: Member['role'], currentRole: Member['role']) => {
      if (!canManageMembers || nextRole === currentRole) {
        return;
      }

      setRoleOverrides((prev) => ({ ...prev, [member.id]: nextRole }));
      setUpdatingMemberId(member.id);
      updateMemberRoleMutation.mutate(
        { memberId: member.id, role: nextRole },
        {
          onError: (error) => {
            const message = error instanceof Error ? error.message : 'Unknown error';
            console.error('Failed to update member role', error);
            window.alert(`Failed to update role: ${message}`);
            setRoleOverrides((prev) => {
              const next = { ...prev };
              delete next[member.id];
              return next;
            });
          },
          onSettled: () => {
            setUpdatingMemberId((current) => (current === member.id ? null : current));
          },
        }
      );
    },
    [canManageMembers, updateMemberRoleMutation]
  );

  React.useEffect(() => {
    const members = membersQuery.data?.items;
    if (!members || members.length === 0) {
      setRoleOverrides((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev;
        }
        return {};
      });
      return;
    }

    setRoleOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      const ids = new Set(members.map((member) => member.id));

      for (const member of members) {
        if (next[member.id] && next[member.id] === member.role) {
          delete next[member.id];
          changed = true;
        }
      }

      for (const id of Object.keys(next)) {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [membersQuery.data]);

  // Team members table columns
  const memberColumns = React.useMemo<ColumnDef<StockFeatures, Member>[]>(() => {
    const columns: ColumnDef<StockFeatures, Member>[] = [
      {
        accessorKey: 'displayName',
        header: 'Name',
        cell: ({ row }) => (
          <div>
            <Flex align="center" gap="xs">
              <Text weight="medium">{row.original.displayName || 'N/A'}</Text>
              {!row.original.isActive && (
                <TagBadge name="Deactivated" color="red" hideDot size="sm" />
              )}
            </Flex>
            {row.original.primaryEmail && (
              <Text size="sm" className="text-muted-foreground">
                {row.original.primaryEmail}
              </Text>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => {
          const selectedRole = roleOverrides[row.original.id] ?? row.original.role;

          if (!canManageMembers) {
            return (
              <TagBadge
                name={row.original.role === 'admin' ? 'Admin' : 'Member'}
                color={badgeVariantToTagColor(
                  row.original.role === 'admin' ? 'default' : 'outline'
                )}
                hideDot
              />
            );
          }

          return (
            <EditableBadge<Member['role']>
              value={selectedRole}
              options={roleBadgeOptions}
              onChange={(nextRole) => handleRoleChange(row.original, nextRole, selectedRole)}
              getVariant={(v) => (v === 'admin' ? 'default' : 'outline')}
              size="sm"
              isLoading={updatingMemberId === row.original.id}
              aria-label={`Role for ${row.original.displayName ?? row.original.primaryEmail ?? 'member'}`}
            />
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined',
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ];

    // Add actions column for admins
    if (canManageMembers) {
      // Admin-only: who still owes enrollment is only actionable by an admin,
      // and publishing which colleagues lack a second factor tells anyone with
      // a foothold which accounts are the softest target.
      columns.push({
        id: 'mfaEnrolled',
        header: 'MFA',
        cell: ({ row }) =>
          row.original.mfaEnrolled ? (
            <TagBadge name="Enrolled" color="green" hideDot size="sm" />
          ) : (
            <TagBadge name="Not enrolled" color="gray" hideDot size="sm" />
          ),
      });

      columns.push({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const isCurrentUser = row.original.userId === user?.id;
          if (isCurrentUser) {
            return null;
          }
          const name = row.original.displayName || row.original.primaryEmail || 'this member';
          return (
            <Flex gap="xs">
              {row.original.mfaEnrolled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    if (
                      window.confirm(
                        `Reset MFA for ${name}? They will have to set up their authenticator app again before they can sign in.`
                      )
                    ) {
                      resetMemberMfaMutation.mutate(row.original.id);
                    }
                  }}
                  isDisabled={resetMemberMfaMutation.isPending}
                  aria-label={`Reset MFA for ${name}`}
                >
                  <ShieldSlash size={16} className="text-muted-foreground" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  if (window.confirm(`Are you sure you want to remove ${name} from the team?`)) {
                    removeMemberMutation.mutate(row.original.id);
                  }
                }}
                isDisabled={removeMemberMutation.isPending}
                aria-label={`Remove ${name}`}
              >
                <Trash size={16} className="text-destructive" />
              </Button>
            </Flex>
          );
        },
      });
    }

    return columns;
  }, [
    canManageMembers,
    handleRoleChange,
    removeMemberMutation,
    resetMemberMfaMutation,
    roleBadgeOptions,
    roleOverrides,
    updatingMemberId,
    user?.id,
  ]);

  const pendingInviteColumns = React.useMemo<ColumnDef<StockFeatures, PendingInvite>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <Text weight="medium">
            {row.original.email ?? (
              <span className="text-muted-foreground italic">Link-only invite</span>
            )}
          </Text>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.role === 'admin' ? 'Admin' : 'Member'}
            color={badgeVariantToTagColor(row.original.role === 'admin' ? 'default' : 'outline')}
            hideDot
          />
        ),
      },
      {
        id: 'invitedBy',
        header: 'Invited by',
        cell: ({ row }) => {
          const { displayName, primaryEmail } = row.original.invitedBy;
          return (
            <Text size="sm" className="text-muted-foreground">
              {displayName || primaryEmail || 'Unknown'}
            </Text>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Sent',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDistanceToNow(new Date(row.original.createdAt), { addSuffix: true })}
          </Text>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDistanceToNow(new Date(row.original.expiresAt), { addSuffix: true })}
          </Text>
        ),
      },
    ],
    []
  );

  if (orgQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  return (
    <Page>
      {currentSection === 'organization' && (
        <Box className="space-y-8">
          {/* Organization Name & Actions */}
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              {/* No "Organization Name" label above it: this is the
                  Organization settings page and the value is plainly a name,
                  so the label only cost a line and broke the label-left rhythm
                  the detail rows below establish. */}
              <div className="min-w-0">
                <Heading level={2} className="truncate">
                  {orgQuery.data?.name}
                </Heading>
              </div>
              <AdminButton isAdmin={canManageMembers} tooltipText="Admin access required">
                <Button variant="outline" size="sm" onPress={() => setIsEditOrgOpen(true)}>
                  Rename
                </Button>
              </AdminButton>
            </div>

            <div className="divide-y divide-border">
              {/* Unconditional, unlike its predecessor: an earlier version of
                  this section gated visibility behind
                  `useShowAppSubdomainSection()` (superadmin OR the org already
                  has a custom subdomain), reasoning an unconditional entry
                  would be a dead section for nearly everyone. As one row in
                  this detail list it always has something true to say — it
                  shows the shared address when the org has no custom one — so
                  there's no empty state left to hide. */}
              <WorkspaceAddressRow isSuperadmin={user?.isSuperadmin ?? false} />

              <MetadataItem label="Created">
                <Text size="sm">
                  {orgQuery.data &&
                    new Date(orgQuery.data.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                </Text>
              </MetadataItem>

              <MetadataItem label="Organization ID">
                <div className="flex items-center gap-1.5">
                  <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono text-muted-foreground">
                    {orgQuery.data?.id}
                  </code>
                  {orgQuery.data?.id && (
                    <CopyValueButton value={orgQuery.data.id} label="Organization ID" />
                  )}
                </div>
              </MetadataItem>

              <MetadataItem label="Your role">
                <TagBadge
                  name={user?.role === 'admin' ? 'Admin' : 'Member'}
                  color={badgeVariantToTagColor(user?.role === 'admin' ? 'default' : 'outline')}
                  hideDot
                  size="sm"
                />
              </MetadataItem>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {orgStatsQuery.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 rounded-lg border bg-card animate-pulse">
                  <div className="h-4 w-16 bg-muted rounded mb-2" />
                  <div className="h-7 w-8 bg-muted rounded" />
                </div>
              ))
            ) : (
              <>
                <div className="p-4 rounded-lg border bg-card">
                  <Text size="sm" className="text-muted-foreground">
                    Members
                  </Text>
                  <Text className="text-2xl font-semibold tabular-nums">
                    {orgStatsQuery.data?.memberCount ?? 0}
                  </Text>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <Text size="sm" className="text-muted-foreground">
                    Applications
                  </Text>
                  <Text className="text-2xl font-semibold tabular-nums">
                    {orgStatsQuery.data?.propertyCount ?? 0}
                  </Text>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <Text size="sm" className="text-muted-foreground">
                    Issues
                  </Text>
                  <Text className="text-2xl font-semibold tabular-nums">
                    {orgStatsQuery.data?.issueCount ?? 0}
                  </Text>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <Text size="sm" className="text-muted-foreground">
                    Integrations
                  </Text>
                  <Text className="text-2xl font-semibold tabular-nums">
                    {orgStatsQuery.data?.integrationCount ?? 0}
                  </Text>
                </div>
              </>
            )}
          </div>

          <OrgMfaRequirementToggle isAdmin={canManageMembers} />

          <PlanFeaturesCard />

          {/* Danger Zone */}
          <DangerSection>
            <DangerAction
              title="Delete Organization"
              description="Permanently delete this organization and all its data. This action cannot be undone."
            >
              <AdminButton isAdmin={canManageMembers} tooltipText="Admin access required">
                <Button
                  variant="destructive"
                  onPress={() => setIsDeleteOrgOpen(true)}
                  iconLeft={<Trash size={14} />}
                >
                  Delete Organization
                </Button>
              </AdminButton>
            </DangerAction>
          </DangerSection>
        </Box>
      )}

      {currentSection === 'jira' && (
        <JiraSettingsSection
          jiraQuery={jiraQuery}
          disconnectJiraMutation={disconnectJiraMutation}
        />
      )}

      {currentSection === 'linear' && (
        <LinearSettingsSection
          linearQuery={linearQuery}
          disconnectLinearMutation={disconnectLinearMutation}
        />
      )}

      {currentSection === 'azure-devops' && (
        <AzureDevopsSettingsSection
          azureDevopsQuery={azureDevopsQuery}
          disconnectAzureDevopsMutation={disconnectAzureDevopsMutation}
        />
      )}

      {currentSection === 'slack' && (
        <DocsAnnotation slug="integrations/slack" title="Slack Integration">
          <Box className="space-y-6">
            {slackQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-8">
                <LoadingOrbit size="md" />
              </Flex>
            ) : slackQuery.data?.ok ? (
              <div className="space-y-4">
                <Card padding="lg">
                  <CardHeader>
                    <Flex align="start" gap="md">
                      <div className="p-2 bg-success/10 rounded-lg">
                        <CheckCircle size={24} className="text-success" />
                      </div>
                      <div className="flex-1">
                        <Flex align="center" gap="sm">
                          <Heading level={3}>Slack Integration</Heading>
                          <TagBadge name="Connected" color="blue" hideDot />
                        </Flex>
                        <Text size="sm" className="text-muted-foreground mt-1">
                          Connected to workspace: <strong>{slackQuery.data.teamName}</strong>
                        </Text>
                      </div>
                      <AdminButton
                        isAdmin={canManageMembers}
                        tooltipText="Admin access required to manage integrations"
                      >
                        <Button
                          variant="destructive"
                          size="sm"
                          onPress={() => {
                            if (window.confirm('Are you sure you want to disconnect Slack?')) {
                              disconnectSlackMutation.mutate();
                            }
                          }}
                          isDisabled={disconnectSlackMutation.isPending}
                        >
                          {disconnectSlackMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      </AdminButton>
                    </Flex>
                  </CardHeader>
                </Card>

                {/* Channel Selection */}
                <Card padding="lg">
                  <CardHeader>
                    <Heading level={4}>Notification Channel</Heading>
                    <Text size="sm" className="text-muted-foreground mt-1">
                      Select the channel where notifications will be sent
                    </Text>
                  </CardHeader>
                  <CardContent>
                    {slackChannelsQuery.isLoading ? (
                      <Flex align="center" gap="sm">
                        <LoadingOrbit size="sm" />
                        <Text size="sm" className="text-muted-foreground">
                          Loading channels...
                        </Text>
                      </Flex>
                    ) : slackChannelsQuery.data?.channels ? (
                      <Flex direction="column" gap="md">
                        <Select
                          aria-label="Select notification channel"
                          selectedKey={slackQuery.data.channelName || ''}
                          items={slackChannelsQuery.data.channels
                            .filter((c) => !c.is_private || c.is_member)
                            .map((c) => ({
                              id: c.id,
                              label: `#${c.name}${c.is_private ? ' (private)' : ''}`,
                            }))}
                          placeholder="Select a channel..."
                          className="max-w-sm"
                          isDisabled={updateSlackChannelMutation.isPending}
                          onSelectionChange={(key) => {
                            const channel = slackChannelsQuery.data?.channels?.find(
                              (c) => c.id === key
                            );
                            if (channel) {
                              updateSlackChannelMutation.mutate({
                                channelId: channel.id,
                                channelName: channel.name,
                              });
                            }
                          }}
                        />
                        {slackQuery.data.channelName && (
                          <Flex align="center" gap="sm">
                            <CheckCircle size={16} className="text-success" />
                            <Text size="sm">
                              Currently sending to: <strong>#{slackQuery.data.channelName}</strong>
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    ) : (
                      <Text size="sm" className="text-muted-foreground">
                        Unable to load channels. Make sure the bot is added to channels you want to
                        use.
                      </Text>
                    )}
                  </CardContent>
                </Card>

                {/* Test Message */}
                <Card padding="lg">
                  <CardHeader>
                    <Heading level={4}>Test Connection</Heading>
                    <Text size="sm" className="text-muted-foreground mt-1">
                      Send a test message to verify the integration is working
                    </Text>
                  </CardHeader>
                  <CardContent>
                    <Flex align="center" gap="md">
                      <Button
                        variant="outline"
                        onPress={() => sendSlackTestMutation.mutate()}
                        isDisabled={sendSlackTestMutation.isPending || !slackQuery.data.channelName}
                      >
                        {sendSlackTestMutation.isPending ? 'Sending...' : 'Send Test Message'}
                      </Button>
                      {sendSlackTestMutation.isSuccess && (
                        <Flex align="center" gap="sm" className="text-success">
                          <CheckCircle size={16} />
                          <Text size="sm">Message sent!</Text>
                        </Flex>
                      )}
                      {sendSlackTestMutation.isError && (
                        <Flex align="center" gap="sm" className="text-destructive">
                          <Warning size={16} />
                          <Text size="sm">
                            {sendSlackTestMutation.error instanceof Error
                              ? sendSlackTestMutation.error.message
                              : 'Failed to send'}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                    {!slackQuery.data.channelName && (
                      <Text size="sm" className="text-muted-foreground mt-2">
                        Please select a channel first
                      </Text>
                    )}
                  </CardContent>
                </Card>

                {/* User Attribution & Linking */}
                <Card padding="lg">
                  <CardHeader>
                    <Heading level={4}>User Attribution</Heading>
                    <Text size="sm" className="text-muted-foreground mt-1">
                      How the bot identifies who's making a request in Slack.
                    </Text>
                  </CardHeader>
                  <CardContent>
                    <Flex direction="column" gap="md">
                      <Flex align="start" justify="between" gap="md">
                        <div className="flex-1">
                          <Text weight="medium" size="sm">
                            Require Slack users to link their {name} account
                          </Text>
                          <Text size="sm" className="text-muted-foreground mt-1">
                            When on, the bot will only run write actions (exploratory tests,
                            workflow creation, task management) for Slack users who have linked to a{' '}
                            {name} user. Unlinked users are asked to sign in once to establish the
                            link.
                          </Text>
                        </div>
                        <Switch
                          isDisabled={
                            slackSettingsQuery.isLoading || updateSlackSettingsMutation.isPending
                          }
                          isSelected={
                            slackSettingsQuery.data?.settings?.requireUserLinking === true
                          }
                          onChange={(isSelected) =>
                            updateSlackSettingsMutation.mutate({
                              requireUserLinking: isSelected,
                            })
                          }
                        />
                      </Flex>

                      <Box className="rounded-md border border-border bg-muted/30 p-3">
                        <Text
                          size="xs"
                          weight="medium"
                          className="uppercase tracking-wide text-muted-foreground mb-2"
                        >
                          What gets attributed
                        </Text>
                        <Flex direction="column" gap="xs">
                          <Text size="sm" className="text-muted-foreground">
                            • <strong>Exploratory tests</strong> created from Slack are recorded as
                            run by the linked user.
                          </Text>
                          <Text size="sm" className="text-muted-foreground">
                            • <strong>Workflows and test runs</strong> kicked off from Slack show
                            the linked user as author.
                          </Text>
                          <Text size="sm" className="text-muted-foreground">
                            • <strong>Agent tasks</strong> created, claimed, or completed via the
                            bot are attributed to the linked user.
                          </Text>
                          <Text size="sm" className="text-muted-foreground">
                            • When a Slack user is unlinked (and this toggle is off), we fall back
                            to the workspace installer or the first org admin — which makes audit
                            trails ambiguous.
                          </Text>
                        </Flex>
                      </Box>
                    </Flex>
                  </CardContent>
                </Card>

                {user?.isSuperadmin && slackSettingsQuery.data?.codexEligible && (
                  <Card padding="lg">
                    <CardHeader>
                      <Heading level={4}>Codex Slack Channel</Heading>
                      <Text size="sm" className="text-muted-foreground mt-1">
                        Choose the internal channel where your Codex workspace can receive requests.
                        Only the configured Slack user can start or steer Codex sessions.
                      </Text>
                    </CardHeader>
                    <CardContent>
                      {slackSettingsQuery.isLoading ? (
                        <Flex align="center" gap="sm">
                          <LoadingOrbit size="sm" />
                          <Text size="sm" className="text-muted-foreground">
                            Loading Codex settings...
                          </Text>
                        </Flex>
                      ) : (
                        <Flex direction="column" gap="md" className="max-w-lg">
                          <Select
                            label="Codex channel"
                            aria-label="Codex Slack channel"
                            selectedKey={codexSettingsDraft.channelId || null}
                            items={
                              slackChannelsQuery.data?.channels
                                ?.filter((channel) => !channel.is_private || channel.is_member)
                                .map((channel) => ({
                                  id: channel.id,
                                  label: `#${channel.name}${channel.is_private ? ' (private)' : ''}`,
                                })) ?? []
                            }
                            placeholder="Select a channel..."
                            onSelectionChange={(key) =>
                              setCodexSettingsDraft((current) => ({
                                ...current,
                                channelId: key ? String(key) : '',
                              }))
                            }
                          />
                          <TextField
                            label="Owner Slack user ID"
                            aria-label="Codex owner Slack user ID"
                            value={codexSettingsDraft.ownerSlackUserId}
                            placeholder="U012ABCDEF3"
                            description="Find this ID in the Slack member profile under the ⋯ menu."
                            onChange={(ownerSlackUserId) =>
                              setCodexSettingsDraft((current) => ({
                                ...current,
                                ownerSlackUserId,
                              }))
                            }
                          />
                          <Flex align="center" gap="md">
                            <Button
                              variant="primary"
                              size="sm"
                              onPress={() =>
                                updateCodexSlackSettingsMutation.mutate({
                                  codexChannelId: codexSettingsDraft.channelId,
                                  codexOwnerSlackUserId: codexSettingsDraft.ownerSlackUserId.trim(),
                                })
                              }
                              isDisabled={
                                updateCodexSlackSettingsMutation.isPending ||
                                !codexSettingsDraft.channelId ||
                                !codexSettingsDraft.ownerSlackUserId.trim()
                              }
                            >
                              {updateCodexSlackSettingsMutation.isPending
                                ? 'Saving...'
                                : 'Save Codex settings'}
                            </Button>
                            {updateCodexSlackSettingsMutation.isSuccess && (
                              <Text size="sm" className="text-success">
                                Settings saved
                              </Text>
                            )}
                            {updateCodexSlackSettingsMutation.isError && (
                              <Text size="sm" className="text-destructive">
                                Could not save Codex settings.
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card padding="lg">
                <CardHeader>
                  <Flex align="start" gap="md">
                    <div className="p-2 bg-warning/10 rounded-lg">
                      <Warning size={24} className="text-warning" />
                    </div>
                    <div className="flex-1">
                      <Flex align="center" gap="sm">
                        <Heading level={3}>Slack Integration</Heading>
                        <TagBadge name="Not Connected" color="yellow" hideDot />
                      </Flex>
                      <Text size="sm" className="text-muted-foreground mt-1">
                        Connect Slack to receive notifications about issues and test results
                      </Text>
                    </div>
                  </Flex>
                </CardHeader>
                <CardContent>
                  <AdminButton
                    isAdmin={canManageMembers}
                    tooltipText="Admin access required to manage integrations"
                  >
                    <Button
                      variant="primary"
                      onPress={startSlackOAuth}
                    >
                      <SlackLogo size={20} className="mr-2" />
                      Add to Slack
                    </Button>
                  </AdminButton>
                </CardContent>
              </Card>
            )}
          </Box>
        </DocsAnnotation>
      )}

      {currentSection === 'teams' && (
        <DocsAnnotation slug="integrations/teams" title="Microsoft Teams Integration">
          <TeamsSettingsSection />
        </DocsAnnotation>
      )}

      {currentSection === 'github' && (
        <Box className="space-y-6">
          {githubInstallError && <Banner variant="error">{githubInstallError}</Banner>}
          {githubQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="md" />
            </Flex>
          ) : githubQuery.data?.ok ? (
            <div className="space-y-4">
              <Card padding="lg">
                <CardHeader>
                  <Flex align="start" gap="md">
                    <div className="p-2 bg-success/10 rounded-lg">
                      <CheckCircle size={24} className="text-success" />
                    </div>
                    <div className="flex-1">
                      <Flex align="center" gap="sm">
                        <Heading level={3}>GitHub Integration</Heading>
                        <TagBadge name="Connected" color="blue" hideDot />
                      </Flex>
                      <Text size="sm" className="text-muted-foreground mt-1">
                        Connected to account: <strong>{githubQuery.data.accountLogin}</strong>
                      </Text>
                    </div>
                    <Flex gap="sm">
                      <Button
                        variant="secondary"
                        size="sm"
                        onPress={() => {
                          startGithubInstall();
                        }}
                      >
                        <ArrowSquareOut size={16} className="mr-1.5" />
                        Manage
                      </Button>
                      <AdminButton
                        isAdmin={canManageMembers}
                        tooltipText="Admin access required to manage integrations"
                      >
                        <Button
                          variant="destructive"
                          size="sm"
                          onPress={() => {
                            if (window.confirm('Are you sure you want to disconnect GitHub?')) {
                              disconnectGitHubMutation.mutate();
                            }
                          }}
                          isDisabled={disconnectGitHubMutation.isPending}
                        >
                          {disconnectGitHubMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      </AdminButton>
                    </Flex>
                  </Flex>
                </CardHeader>
              </Card>

              {/* Connected Repositories */}
              <Card padding="lg">
                <CardHeader>
                  <Heading level={4}>Connected Repositories</Heading>
                  <Text size="sm" className="text-muted-foreground mt-1">
                    Repositories accessible through this GitHub App installation
                  </Text>
                </CardHeader>
                <CardContent>
                  {githubReposQuery.isLoading ? (
                    <Flex align="center" gap="sm">
                      <LoadingOrbit size="sm" />
                      <Text size="sm" className="text-muted-foreground">
                        Loading repositories...
                      </Text>
                    </Flex>
                  ) : githubReposQuery.data?.repositories &&
                    githubReposQuery.data.repositories.length > 0 ? (
                    <div className="space-y-2">
                      {githubReposQuery.data.repositories.map((repo) => (
                        <div
                          key={repo.id}
                          className="flex items-center justify-between py-3 border-b border-border last:border-0"
                        >
                          <div className="flex items-center gap-3">
                            <GithubLogo size={20} className="text-muted-foreground" />
                            <div>
                              <a
                                href={repo.htmlUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-primary hover:underline"
                              >
                                {repo.fullName}
                              </a>
                              <Text size="sm" className="text-muted-foreground">
                                Default branch: {repo.defaultBranch}
                              </Text>
                            </div>
                          </div>
                          <TagBadge
                            name={repo.private ? 'Private' : 'Public'}
                            color={badgeVariantToTagColor(repo.private ? 'outline' : 'default')}
                            hideDot
                            size="sm"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text size="sm" className="text-muted-foreground">
                      No repositories found. You may need to configure repository access in your
                      GitHub App settings.
                    </Text>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card padding="lg">
              <CardContent>
                <Flex direction="column" align="center" justify="center" className="py-8">
                  <img
                    src={octocatImage}
                    alt="GitHub Octocat"
                    className="w-48 h-48 object-contain mb-2"
                  />
                  <Heading level={3} className="text-center mb-2">
                    Connect GitHub
                  </Heading>
                  <Text size="sm" className="text-muted-foreground text-center max-w-md mb-2">
                    Connect GitHub to receive pull request webhooks and post check run results on
                    your repositories
                  </Text>
                  <Text size="sm" className="text-muted-foreground text-center max-w-md mb-6">
                    {GITHUB_ONE_ORG_EXPLAINER}
                  </Text>
                  <AdminButton
                    isAdmin={canManageMembers}
                    tooltipText="Admin access required to manage integrations"
                  >
                    <Button
                      variant="primary"
                      onPress={() => {
                        startGithubInstall();
                      }}
                    >
                      <GithubLogo size={20} className="mr-2" />
                      Install GitHub App
                    </Button>
                  </AdminButton>
                </Flex>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {currentSection === 'team' && (
        <Box className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Heading level={2}>Team Members</Heading>
              <Text size="sm" className="text-muted-foreground">
                {membersQuery.data?.items?.length ?? 0}{' '}
                {membersQuery.data?.items?.length === 1 ? 'member' : 'members'} with access to this
                organization
              </Text>
            </div>
            <AdminButton
              isAdmin={canManageMembers}
              tooltipText="Admin access required to invite members"
            >
              <Button
                variant="primary"
                onPress={() => {
                  // Reset modal state
                  setInviteEmail('');
                  setInviteRole('member');
                  setInviteLink('');
                  setInviteEmailSent(false);
                  setIsInviteDialogOpen(true);
                }}
              >
                Invite Member
              </Button>
            </AdminButton>
          </div>

          {canManageMembers &&
            pendingInvitesQuery.data?.items &&
            pendingInvitesQuery.data.items.length > 0 && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Heading level={3}>Pending Invites</Heading>
                  <Text size="sm" className="text-muted-foreground">
                    {pendingInvitesQuery.data.items.length}{' '}
                    {pendingInvitesQuery.data.items.length === 1 ? 'invite' : 'invites'} awaiting
                    acceptance
                  </Text>
                </div>
                <DataTable
                  columns={pendingInviteColumns}
                  data={pendingInvitesQuery.data.items}
                  aria-label="Pending invites table"
                />
              </div>
            )}

          {membersQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-12">
              <LoadingOrbit size="md" />
            </Flex>
          ) : membersQuery.data?.items && membersQuery.data.items.length > 0 ? (
            <DataTable
              columns={memberColumns}
              data={membersQuery.data.items}
              aria-label="Team members table"
            />
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
              <UserIcon size={48} className="mx-auto text-muted-foreground mb-4" />
              <Text className="text-muted-foreground">No team members found</Text>
            </div>
          )}
        </Box>
      )}

      {currentSection === 'issue-categories' && <IssueCategoriesSection isAdmin={true} />}

      {/* Members may write tags (no admin check on the tag routes). */}
      {currentSection === 'tags' && <TagsSection isAdmin={true} />}

      {currentSection === 'workflows' && <WorkflowsSection isAdmin={canManageMembers} />}

      {currentSection === 'observability' && (
        <ObservabilitySection isAdmin={canManageMembers} />
      )}
      {currentSection === 'agent-browser' && (
        <AgentBrowserSection isAdmin={canManageMembers} />
      )}

      {currentSection === 'notifications' && (
        <NotificationsSection personalAlertsEnabled={!!user?.features?.notifications} />
      )}

      {currentSection === 'digests' && <DigestsSection isAdmin={canManageMembers} />}

      {currentSection === 'api-keys' && <ApiKeysSection isAdmin={canManageMembers} />}

      {currentSection === 'test-sign-in' && user?.features?.testSignIn && (
        <TestSignInSection isAdmin={canManageMembers} />
      )}

      {currentSection === 'security' && <SecuritySection />}

      {currentSection === 'cloudflare-access' && <CloudflareAccessSection isAdmin={canManageMembers} />}

      {currentSection === 'custom-domains' && user?.features?.sandboxCustomDomains && (
        <CustomDomainsSection isAdmin={canManageMembers} />
      )}

      {currentSection === 'claude' && <ClaudeSettingsSection />}

      {currentSection === 'aws' && <AwsConnectionSection isAdmin={canManageMembers} />}

      {currentSection === 'documentation' && (
        <DocumentationSection isAdmin={canManageMembers} />
      )}

      {currentSection === 'usage' && <UsageSection />}

      {currentSection === 'billing' && <BillingSection />}

      {currentSection === 'features' && user?.isSuperadmin && <FeatureFlagsSection />}

      {currentSection === 'inbox' && user?.isSuperadmin && (
        <Box className="space-y-6">
          <DomainAllowlistManager />
        </Box>
      )}

      {/* Edit Organization Dialog */}
      <DialogTrigger isOpen={isEditOrgOpen} onOpenChange={setIsEditOrgOpen}>
        <span />
        <Dialog title="Edit Organization">
          <DialogContent>
            <TextField
              label="Organization Name"
              value={orgName}
              onChange={setOrgName}
              isDisabled={updateOrgMutation.isPending}
              autoFocus
            />
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsEditOrgOpen(false)}
              isDisabled={updateOrgMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => updateOrgMutation.mutate(orgName)}
              isDisabled={updateOrgMutation.isPending || !orgName.trim()}
            >
              {updateOrgMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Invite Member Dialog */}
      <DialogTrigger isOpen={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <span />
        <Dialog title="Invite Team Member">
          <DialogContent>
            {inviteEmailSent ? (
              // Success state after email sent
              <div className="text-center py-4">
                <div className="p-3 bg-success/10 rounded-full inline-block mb-4">
                  <CheckCircle size={32} className="text-success" />
                </div>
                <Heading level={4} className="mb-2">
                  Invitation Sent
                </Heading>
                <Text size="sm" className="text-muted-foreground">
                  We&apos;ve sent an invitation email to <strong>{inviteEmail}</strong>. The link
                  expires in 7 days.
                </Text>
              </div>
            ) : (
              // Email-first view with copy link option
              <>
                <Text size="sm" className="text-muted-foreground mb-4">
                  Enter the email address of the team member you want to invite.
                </Text>
                <TextField
                  label="Email Address"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={setInviteEmail}
                  isDisabled={createInviteMutation.isPending}
                  autoFocus
                />
                <div className="mt-4">
                  <Select
                    label="Role"
                    items={[
                      { id: 'member', label: 'Member' },
                      { id: 'admin', label: 'Admin' },
                    ]}
                    selectedKey={inviteRole}
                    onSelectionChange={(key) => setInviteRole(key as 'admin' | 'member')}
                    isDisabled={createInviteMutation.isPending}
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <Text size="sm" className="text-muted-foreground mb-2">
                    Or copy a shareable link instead:
                  </Text>
                  <Button
                    variant="outline"
                    size="sm"
                    isDisabled={createInviteMutation.isPending}
                    onPress={() => {
                      if (inviteLink) {
                        navigator.clipboard.writeText(inviteLink);
                        toast.success('Invite link copied to clipboard');
                      } else {
                        createInviteMutation.mutate(
                          { role: inviteRole },
                          {
                            onSuccess: (data) => {
                              const link = data.inviteUrl;
                              navigator.clipboard.writeText(link);
                              toast.success('Invite link copied to clipboard');
                            },
                          }
                        );
                      }
                    }}
                  >
                    Copy Invite Link
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
          <DialogFooter>
            {inviteEmailSent ? (
              <>
                <Button
                  variant="outline"
                  onPress={() => {
                    // Allow sending another invite
                    setInviteEmail('');
                    setInviteRole('member');
                    setInviteLink('');
                    setInviteEmailSent(false);
                  }}
                >
                  Invite Another
                </Button>
                <Button variant="primary" onPress={() => setIsInviteDialogOpen(false)}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onPress={() => setIsInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onPress={() =>
                    createInviteMutation.mutate({ email: inviteEmail, role: inviteRole })
                  }
                  isDisabled={
                    createInviteMutation.isPending ||
                    !inviteEmail.trim() ||
                    !inviteEmail.includes('@')
                  }
                >
                  {createInviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                </Button>
              </>
            )}
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Delete Organization Dialog */}
      <DialogTrigger isOpen={isDeleteOrgOpen} onOpenChange={setIsDeleteOrgOpen}>
        <span />
        <Dialog title="Delete Organization">
          <DialogContent>
            <div className="space-y-4">
              <Text>
                This action <strong>cannot be undone</strong>. This will permanently delete{' '}
                <strong>{orgQuery.data?.name}</strong> and all associated data including:
              </Text>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>All applications and their configurations</li>
                <li>All issues and their history</li>
                <li>All integrations (Jira, Slack, GitHub)</li>
                <li>All team member associations</li>
              </ul>
              <Text size="sm" className="text-destructive">
                All members will be signed out immediately.
              </Text>
              <div className="space-y-2 pt-2">
                <Text size="sm">
                  Type <strong>{orgQuery.data?.name}</strong> to confirm:
                </Text>
                <TextField
                  aria-label="Organization name confirmation"
                  placeholder={orgQuery.data?.name}
                  value={deleteConfirmName}
                  onChange={setDeleteConfirmName}
                />
              </div>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setIsDeleteOrgOpen(false);
                setDeleteConfirmName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              isDisabled={deleteConfirmName !== orgQuery.data?.name || deleteOrgMutation.isPending}
              onPress={() => deleteOrgMutation.mutate()}
            >
              {deleteOrgMutation.isPending ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Page>
  );
}
